import React, { useEffect, useState, useRef } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import IRIBufferingForm from 'routes/IRIBuffering/components/IRIBufferingForm';
import { Ref } from 'routes/IRIBuffering/components/IRIBufferingForm/IRIBufferingForm';
import { RootState } from 'data/root.reducers';
import * as selectors from 'data/iriBuffering/iriBuffering.selectors';
import * as actions from 'data/iriBuffering/iriBuffering.actions';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import { IRIBuffering } from 'data/iriBuffering/iriBuffering.types';
import { useTranslation } from 'react-i18next';
import { iriBufferingTemplate } from '../../templates';
import { adaptTemplateVisibilityMode } from '../../utils/IRIBuffering.adapter';

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  iriBufferingTemplateDef: adaptTemplateVisibilityMode(iriBufferingTemplate, 'view'),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserId(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  selectedIRIBuffering: selectors.getSelectedIRIBuffering(state)[0],
  iriBuffering: selectors.getIRIBuffering(state),
  originalSelectedIRIBuffering: selectors.getSelectedIRIBuffering(state)[0],
});

const mapDispatch = {
  updateIRIBuffering: actions.updateIRIBuffering,
  getIRIBuffering: actions.getIRIBuffering,
  goToIRIBufferingEdit: actions.goToIRIBufferingEdit,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const IRIBufferingView = ({
  history,
  iriBuffering,
  deliveryFunctionId,
  iriBufferingTemplateDef,
  goToIRIBufferingEdit,
  getIRIBuffering,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<IRIBuffering>>({});
  const [template, setTemplate] = useState(iriBufferingTemplateDef);
  const { t } = useTranslation();

  useEffect(() => {
    getIRIBuffering(deliveryFunctionId);
  }, [deliveryFunctionId, getIRIBuffering]);

  useEffect(() => {
    if (iriBuffering != null && template != null) {
      const clonedTemplate = cloneDeep(template);
      setFormData(iriBuffering);
      clonedTemplate.formId = Date.now().toString();
      clonedTemplate.key = Date.now().toString();
      setTemplate(clonedTemplate);
    }
  }, [
    iriBuffering,
  ]);

  const handleClickEdit = () => goToIRIBufferingEdit(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as IRIBuffering);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <IRIBufferingForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = () => {
    return (
      <FormPanel>
        <FormButtons
          isEditing={false}
          onClickEdit={handleClickEdit}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="IRIBufferingView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel()}
          <div className="read-only">
            {renderForm()}
          </div>
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(IRIBufferingView));
